import { useState } from "react";
import { Flex } from "antd";
import { useTranslate } from "@refinedev/core";
import { Modal, Select } from "@libertify/ui";
import type { UserRoleData } from "hooks/useUserRoles";
import type { ScimGroupMapping, Workspace } from "@/types";
import {
  getGroupName,
  type MappingEntry,
} from "../../helpers/scimGroupMappings";

// Select value for "workspace membership without a role"
const NO_ROLE = "no-role";

export type GroupMappingModalProps = {
  /** Groups offered in the group select; only used when adding */
  groups: ScimGroupMapping[];
  /** Set when editing: the group is fixed */
  initialGroupId?: string;
  initialEntry?: MappingEntry;
  roles: UserRoleData[];
  workspaces: Workspace[];
  isSaving: boolean;
  onClose: () => void;
  onSubmit: (groupId: string, entry: MappingEntry) => void;
};

export const GroupMappingModal = ({
  groups,
  initialGroupId,
  initialEntry,
  roles,
  workspaces,
  isSaving,
  onClose,
  onSubmit,
}: GroupMappingModalProps) => {
  const t = useTranslate();
  const isEditing = Boolean(initialEntry);
  const [groupId, setGroupId] = useState(initialGroupId);
  const [roleValue, setRoleValue] = useState<string | undefined>(
    initialEntry ? (initialEntry.roleId ?? NO_ROLE) : undefined
  );
  const [workspaceIds, setWorkspaceIds] = useState<string[]>(
    initialEntry?.level === "workspace" ? initialEntry.workspaceIds : []
  );

  const organizationRoles = roles.filter(
    (role) => role.level === "organization"
  );
  const workspaceRoles = roles.filter((role) => role.level === "workspace");
  const isOrganizationRole = organizationRoles.some(
    (role) => role.id === roleValue
  );
  const needsWorkspaces = Boolean(roleValue) && !isOrganizationRole;
  const canSubmit =
    Boolean(groupId) &&
    Boolean(roleValue) &&
    (isOrganizationRole || workspaceIds.length > 0);

  const handleSubmit = () => {
    if (!groupId || !roleValue) {
      return;
    }
    onSubmit(
      groupId,
      isOrganizationRole
        ? { level: "organization", roleId: roleValue }
        : {
            level: "workspace",
            roleId: roleValue === NO_ROLE ? null : roleValue,
            workspaceIds,
          }
    );
  };

  return (
    <Modal
      open
      title={t(
        isEditing
          ? "media.organizations.groupMappings.modal.editTitle"
          : "media.organizations.groupMappings.modal.addTitle"
      )}
      confirmText={t("media.organizations.groupMappings.modal.save")}
      cancelText={t("media.organizations.index.cancel")}
      confirmLoading={isSaving}
      confirmDisabled={!canSubmit}
      onClose={onClose}
      onConfirm={handleSubmit}
    >
      <Flex vertical gap={16}>
        <Select
          label={t("media.organizations.groupMappings.columns.group")}
          required
          disabled={isEditing}
          value={groupId}
          onChange={(value: string) => setGroupId(value)}
          showSearch
          optionFilterProp="label"
          options={groups.map((group) => ({
            label: getGroupName(group),
            value: group.directory_group_id,
          }))}
        />
        <Select
          label={t("media.organizations.groupMappings.columns.role")}
          required
          value={roleValue}
          onChange={(value: string) => setRoleValue(value)}
          options={[
            {
              label: t(
                "media.organizations.groupMappings.modal.organizationRoles"
              ),
              options: organizationRoles.map((role) => ({
                label: role.name,
                value: role.id,
              })),
            },
            {
              label: t("media.organizations.groupMappings.modal.workspaceRoles"),
              options: [
                ...workspaceRoles.map((role) => ({
                  label: role.name,
                  value: role.id,
                })),
                {
                  label: t("media.organizations.groupMappings.noRole"),
                  value: NO_ROLE,
                },
              ],
            },
          ]}
        />
        {needsWorkspaces ? (
          <Select
            label={t("media.organizations.groupMappings.columns.workspaces")}
            helpText={t("media.organizations.groupMappings.workspaceRoleHint")}
            required
            mode="multiple"
            value={workspaceIds}
            onChange={(value: string[]) => setWorkspaceIds(value)}
            optionFilterProp="label"
            options={workspaces.map((workspace) => ({
              label: workspace.name,
              value: workspace.id,
            }))}
          />
        ) : null}
      </Flex>
    </Modal>
  );
};
